import React, { useState } from 'react';
import { useThoughtSpotAuth } from '../hooks/useThoughtSpotAuth';
import AppEmbed from './AppEmbed';
import LiveboardEmbed from './LiveboardEmbed';
import BEM from 'common/bem';

const classes = BEM.with('ClinicalWorkflows');

// ThoughtSpot group that controls what permissions embedded users are provisioned with.
// Kept here so product requirements around access level live in the toolkit, not the SDK.
const THOUGHTSPOT_GROUP = 'Application Control';

// Maps each nav view to its ThoughtSpot liveboard identifier (case-insensitive match against the API response)
const LIVEBOARD_IDENTIFIERS: Record<string, string> = {
  'nurse-call': 'Nurse Call',
  consults: 'Consults',
};

type InsightsView = 'my-liveboards' | 'nurse-call' | 'consults';

const ALL_INSIGHTS_VIEWS: { id: InsightsView; label: string }[] = [
  { id: 'my-liveboards', label: 'My Liveboards' },
  { id: 'nurse-call', label: 'Nurse Call' },
  { id: 'consults', label: 'Consults' },
];

type Props = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  client: Record<string, any> | null;
  currentOrganizationId: string;
};

export default function InsightsTab({ client, currentOrganizationId }: Props) {
  const [activeInsightsView, setActiveInsightsView] = useState<InsightsView>('my-liveboards');
  const authState = useThoughtSpotAuth(client, currentOrganizationId, THOUGHTSPOT_GROUP);

  const visibleViews = ALL_INSIGHTS_VIEWS.filter(({ id }) => {
    if (id === 'my-liveboards') return true;
    if (authState.status !== 'ready') return false;
    const identifier = LIVEBOARD_IDENTIFIERS[id];
    return authState.liveboards.some(
      (lb) => lb.identifier.toLowerCase() === identifier.toLowerCase()
    );
  });

  const renderContent = () => {
    if (authState.status === 'loading' || authState.status === 'idle') {
      return <div className={classes('insights-loading')}>Loading analytics…</div>;
    }

    if (authState.status === 'error') {
      return (
        <div className={classes('insights-error')}>
          Unable to load analytics. Please try again later.
        </div>
      );
    }

    if (activeInsightsView === 'my-liveboards') {
      return (
        <AppEmbed
          host={authState.host}
          token={authState.token}
          orgId={authState.orgId}
          onLiveboardSelect={(guid) => {
            const orgLiveboard = authState.liveboards.find((lb) => lb.guid === guid);
            const navView = orgLiveboard
              ? (Object.entries(LIVEBOARD_IDENTIFIERS).find(
                  ([, identifier]) =>
                    identifier.toLowerCase() === orgLiveboard.identifier.toLowerCase()
                )?.[0] as InsightsView | undefined)
              : undefined;
            if (navView) {
              setActiveInsightsView(navView);
            }
          }}
        />
      );
    }

    const identifier = LIVEBOARD_IDENTIFIERS[activeInsightsView];
    const liveboard = authState.liveboards.find(
      (lb) => lb.identifier.toLowerCase() === identifier.toLowerCase()
    );

    if (!liveboard) {
      return (
        <div className={classes('insights-error')}>
          Unable to load analytics. Please try again later.
        </div>
      );
    }

    return (
      <LiveboardEmbed
        liveboardId={liveboard.guid}
        host={authState.host}
        token={authState.token}
        orgId={authState.orgId}
      />
    );
  };

  return (
    <div className={classes('insights-split')} data-test-id="clinical-workflows-insights-content">
      <div className={classes('insights-nav')}>
        {visibleViews.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            className={classes('insights-nav-item', { selected: activeInsightsView === id })}
            onClick={() => setActiveInsightsView(id)}
            data-test-id={`clinical-workflows-insights-${id}`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className={classes('insights-content')}>{renderContent()}</div>
    </div>
  );
}
